import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '../../hooks/useDebounce';
import { useToast } from '../shared/Toast';
import axiosInstance from '../../services/api';
import { sanitizeCaption } from '../../utils/sanitization';

/**
 * CaptionEditor Component
 * Textarea-based editor with auto-save functionality and character count
 * Auto-saves after 800ms of inactivity
 */
export const CaptionEditor = ({
  initialCaption,
  imageId,
  captionId = null,
  instagramAccountId,
  onSave,
  onChange = null
}) => {
  const { t } = useTranslation('approval');
  const toast = useToast();
  const [caption, setCaption] = useState(initialCaption || '');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedCaption, setLastSavedCaption] = useState(initialCaption || '');
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Debounce caption for auto-save (800ms delay)
  const debouncedCaption = useDebounce(caption, 800);

  // Character limit for Instagram captions
  const MAX_CHARACTERS = 2200;
  const characterCount = caption.length;
  const isOverLimit = characterCount > MAX_CHARACTERS;

  /**
   * Handle caption text change
   */
  const handleCaptionChange = (e) => {
    const newCaption = e.target.value;
    setCaption(newCaption);

    // Call onChange callback for live preview updates
    if (onChange) {
      onChange(newCaption);
    }
  };

  /**
   * Handle paste event - ensure proper handling of emojis and special characters
   */
  const handlePaste = (e) => {
    // Let the default paste behavior handle emojis and special characters
    // Just track that a change occurred
    setTimeout(() => {
      const textarea = e.target;
      const newValue = textarea.value;
      if (newValue !== caption) {
        setCaption(newValue);
        if (onChange) {
          onChange(newValue);
        }
      }
    }, 0);
  };

  // Auto-save when debounced caption changes
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const performSave = async () => {
      if (debouncedCaption !== lastSavedCaption && !isOverLimit && isMounted) {
        try {
          setIsSaving(true);
          setShowSavedIndicator(false);

          // Sanitize caption text before sending to API
          const sanitizedCaption = sanitizeCaption(debouncedCaption);

          // Call API to save draft
          const requestData = {
            action: 'save_draft',
            caption: { text: sanitizedCaption },
            instagram_account_id: instagramAccountId,
          };

          // Include captionId if available (for existing captions)
          if (captionId) {
            requestData.previousCaptionId = captionId;
          }

          await axiosInstance.post(`/captions/${imageId}/update`, requestData, {
            signal: abortController.signal
          });

          if (isMounted) {
            // Update last saved caption
            setLastSavedCaption(debouncedCaption);

            // Call onSave callback if provided
            if (onSave) {
              onSave(debouncedCaption);
            }

            // Show saved indicator
            setShowSavedIndicator(true);

            // Hide saved indicator after 2 seconds
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(() => {
              if (isMounted) {
                setShowSavedIndicator(false);
              }
            }, 2000);

            // Show success toast
            toast.success(t('caption.toast.saved'));
          }
        } catch (error) {
          if (error.name !== 'AbortError' && isMounted) {
            console.error('Failed to save caption:', error);
            toast.error(t('caption.toast.error'));
          }
        } finally {
          if (isMounted) {
            setIsSaving(false);
          }
        }
      }
    };

    performSave();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [debouncedCaption, lastSavedCaption, isOverLimit, captionId, imageId, instagramAccountId, onSave, toast, t]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Get character count color
  const getCharacterCountColor = () => {
    if (isOverLimit) return 'text-red-600';
    if (characterCount > MAX_CHARACTERS * 0.9) return 'text-yellow-600';
    return 'text-gray-500';
  };

  // Get saving indicator text
  const getSavingIndicatorText = () => {
    if (isSaving) return t('caption.saving');
    if (showSavedIndicator) return t('caption.saved');
    return null;
  };

  return (
    <div className="caption-editor">
      {/* Header with label and saving indicator */}
      <div className="flex items-center justify-between mb-2">
        <label
          htmlFor="caption-textarea"
          className="block text-sm font-medium text-gray-700"
        >
          {t('caption.label')}
        </label>

        {/* Saving indicator */}
        {getSavingIndicatorText() && (
          <span
            className={`text-sm ${
              isSaving ? 'text-gray-500' : 'text-green-600'
            } flex items-center gap-1`}
            aria-live="polite"
            aria-atomic="true"
          >
            {isSaving && (
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {getSavingIndicatorText()}
          </span>
        )}
      </div>

      {/* Textarea */}
      <textarea
        id="caption-textarea"
        value={caption}
        onChange={handleCaptionChange}
        onPaste={handlePaste}
        rows={6}
        className={`
          w-full px-3 py-2
          border rounded-md
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          ${isOverLimit ? 'border-red-300' : 'border-gray-300'}
          resize-none
          font-sans text-sm leading-relaxed
        `}
        placeholder={t('caption.placeholder')}
        aria-describedby="character-count"
        aria-invalid={isOverLimit}
      />

      {/* Character counter */}
      <div className="mt-2 flex items-center justify-between">
        <div
          id="character-count"
          className={`text-sm ${getCharacterCountColor()}`}
          aria-live="polite"
        >
          {t('caption.counter', { count: characterCount, max: MAX_CHARACTERS })}
          {isOverLimit && (
            <span className="ml-2 text-xs">
              {t('caption.overflow', { excess: characterCount - MAX_CHARACTERS })}
            </span>
          )}
        </div>
      </div>

      {/* Warning message if over limit */}
      {isOverLimit && (
        <div
          className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md"
          role="alert"
        >
          <p className="text-sm text-red-700">
            {t('caption.warning', { max: MAX_CHARACTERS })}
          </p>
        </div>
      )}
    </div>
  );
};

CaptionEditor.propTypes = {
  initialCaption: PropTypes.string.isRequired,
  imageId: PropTypes.string.isRequired,
  captionId: PropTypes.string,
  instagramAccountId: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onChange: PropTypes.func,
};

export default CaptionEditor;